import {
  Bot,
  Building2,
  Calculator,
  DraftingCompass,
  FileText,
  Inbox,
  LayoutGrid,
  Map,
  Settings,
  ShieldCheck,
  Siren,
  Sun,
  Ticket,
} from "lucide-react";
import type * as React from "react";

import type { PrimaryPersona } from "@/api/me";
import { isMobileBuild } from "@/platform";

/**
 * The navigation map (MV2-845, app/Main + ds/Nav).
 *
 * Ten destinations in four verb groups — Work · Understand · Produce ·
 * Operate — grouped by what an operator does, not alphabetically. Every
 * persona sees only the items it owns — the Site Owner gets My plants and
 * Settings (MV2-1261: one shell for everyone). Devices lives on as a Sites view (MV2-848);
 * the quoting tool is its own Produce item, Quote (MV2-914); their old
 * routes redirect. Simulator and Quote are SuperAdmin-only for now (Kranthi,
 * 10 Sep) — the client-grantable `view_simulator` feature stays on the
 * Simulator gate for the day it reopens to ClientAdmin.
 *
 * This map feeds both the desktop `Sidebar` and mobile's `MobileBottomNav`
 * "More" sheet — `filterNav` drops `desktopOnly` items (Admin, Simulator,
 * MLOps, Quote: none has a route in `router.mobile.tsx`) whenever
 * `isMobileBuild` is true, so the desktop rail is untouched.
 */
export interface NavItem {
  to: string;
  label: string;
  icon: React.ComponentType<{ size?: number }>;
  /** Personas that should see this item. */
  personas: readonly PrimaryPersona[];
  /** Policy-feature gate — hidden when `/me.features` lacks it. SuperAdmin always passes. */
  feature?: string;
  /** OR variant of `feature`. */
  anyFeature?: readonly string[];
  /** Second key of the `g` leader — `g s` → Sites. */
  key?: string;
  /**
   * MV2-985 — hidden on the mobile build. Admin, Simulator, MLOps and
   * Quote have no route in `router.mobile.tsx` (see that file's own
   * scope comment), so without this gate the bottom nav's "More" sheet
   * linked to a dead end that fell through the router's `*` catch-all
   * and silently bounced to the dashboard.
   */
  desktopOnly?: boolean;
}

export type NavGroupName = "Work" | "Understand" | "Produce" | "Operate";

export interface NavGroup {
  group: NavGroupName;
  items: NavItem[];
}

const CONSOLE: readonly PrimaryPersona[] = ["SuperAdmin", "ClientAdmin", "OM"];

export const NAV_GROUPS: readonly NavGroup[] = [
  {
    group: "Work",
    items: [
      {
        to: "/inbox",
        label: "Inbox",
        icon: Inbox,
        personas: CONSOLE,
        key: "i",
      },
      {
        to: "/alarms",
        label: "Alarms",
        // Siren, not Bell — the top bar's bell is notifications (Kranthi 10 Sep).
        icon: Siren,
        personas: CONSOLE,
        // Live alarms are the page's job; historical access alone does not open it.
        feature: "view_live_alarms",
        key: "a",
      },
      {
        to: "/tickets",
        label: "Tickets",
        icon: Ticket,
        personas: CONSOLE,
        feature: "view_tickets",
        key: "t",
      },
    ],
  },
  {
    group: "Understand",
    items: [
      {
        to: "/overview",
        label: "Overview",
        icon: LayoutGrid,
        personas: CONSOLE,
        key: "o",
      },
      {
        to: "/sites",
        label: "Sites",
        icon: Building2,
        personas: CONSOLE,
        key: "s",
      },
      {
        to: "/map",
        label: "Map",
        icon: Map,
        personas: CONSOLE,
        feature: "view_map",
        key: "m",
      },
      // The owner's surface, in the rail since MV2-1261; the platform
      // SuperAdmin sees it too, to walk into it (Kranthi, 8 Sep).
      {
        to: "/my-plants",
        label: "My plants",
        icon: Sun,
        personas: ["SiteOwner"],
      },
    ],
  },
  {
    group: "Produce",
    items: [
      {
        to: "/reports",
        label: "Reports",
        icon: FileText,
        personas: CONSOLE,
        anyFeature: ["generate_report", "subscribe_report"],
      },
      {
        to: "/simulator",
        label: "Simulator",
        // A drafting compass says "plan a site"; the flask said "experiment" (Kranthi 10 Sep).
        icon: DraftingCompass,
        personas: ["SuperAdmin"],
        feature: "view_simulator",
        desktopOnly: true,
      },
      // MV2-914 — the quoting tool, SuperAdmin-only (so hidden while acting
      // as a tenant, whose effective persona is ClientAdmin).
      { to: "/quote", label: "Quote", icon: Calculator, personas: ["SuperAdmin"], desktopOnly: true },
    ],
  },
  {
    group: "Operate",
    items: [
      { to: "/ml", label: "MLOps", icon: Bot, personas: ["SuperAdmin"], desktopOnly: true },
      {
        // The /admin index redirects per persona (AdminIndexRedirect).
        to: "/admin",
        label: "Admin",
        icon: ShieldCheck,
        personas: ["SuperAdmin", "ClientAdmin"],
        desktopOnly: true,
      },
      {
        to: "/settings/my-profile",
        label: "Settings",
        icon: Settings,
        personas: [...CONSOLE, "SiteOwner"],
      },
    ],
  },
];

/**
 * Which items a persona gets. SuperAdmin on the platform view sees
 * everything; SuperAdmin acting as a tenant sees exactly what that tenant's
 * ClientAdmin sees (feature gates included). Returns [] while persona is
 * unknown so nothing leaks during the /me refetch after a tenant switch.
 */
export function filterNav(
  groups: readonly NavGroup[],
  persona: PrimaryPersona | null,
  isImpersonating: boolean,
  features: readonly string[] | null,
): NavGroup[] {
  if (persona == null) return [];
  const passesFeature = (i: NavItem): boolean => {
    if (!i.feature && (!i.anyFeature || i.anyFeature.length === 0)) return true;
    if (persona === "SuperAdmin" && !isImpersonating) return true;
    // Fail closed until /me carries the features array.
    if (!features) return false;
    if (i.feature && !features.includes(i.feature)) return false;
    if (i.anyFeature?.length && !i.anyFeature.some((f) => features.includes(f)))
      return false;
    return true;
  };
  const effective: PrimaryPersona =
    persona === "SuperAdmin" && isImpersonating ? "ClientAdmin" : persona;
  const seesAll = persona === "SuperAdmin" && !isImpersonating;
  return groups
    .map((g) => ({
      group: g.group,
      items: g.items.filter(
        (i) =>
          (seesAll || i.personas.includes(effective)) &&
          passesFeature(i) &&
          !(i.desktopOnly && isMobileBuild),
      ),
    }))
    .filter((g) => g.items.length > 0);
}

export function flattenNav(groups: readonly NavGroup[]): NavItem[] {
  return groups.flatMap((g) => g.items);
}

/** Where each persona lands (app/ShellOM, ShellClientAdmin, ShellSuperAdmin). */
export function personaHome(
  persona: PrimaryPersona | string | null | undefined,
  { isMobile = false }: { isMobile?: boolean } = {},
): string {
  if (isMobile) return persona === "SiteOwner" ? "/my-plants" : "/overview";
  switch (persona) {
    case "SuperAdmin":
      // Platform Health's readiness table is the SuperAdmin landing.
      return "/admin/platform/health";
    case "ClientAdmin":
      return "/overview";
    case "OM":
      return "/inbox";
    case "SiteOwner":
      return "/my-plants";
    default:
      return "/inbox";
  }
}

/** The badge in the top bar and the user menu. */
export function personaLabel(
  persona: PrimaryPersona | string | null | undefined,
): string {
  switch (persona) {
    case "SuperAdmin":
      return "SuperAdmin";
    case "ClientAdmin":
      return "ClientAdmin";
    case "OM":
      return "O&M";
    case "SiteOwner":
      return "Site Owner";
    default:
      return "";
  }
}

/** `g` + key → route, built from the items that declare a key. */
export const LEADER_TARGETS: Readonly<Record<string, string>> =
  Object.fromEntries(
    flattenNav(NAV_GROUPS)
      .filter((i) => i.key)
      .map((i) => [i.key as string, i.to]),
  );
